import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

const VARIANT_TITLES_SELECTOR = 'script[data-back-in-stock-variants]';
const CONTENT_SELECTOR = '[data-back-in-stock-content]';
const SUCCESS_SELECTOR = '[data-back-in-stock-success]';
const ERROR_SELECTOR = '[data-back-in-stock-error]';
const FORM_SELECTOR = '.back-in-stock__form';
const CONTACT_ENDPOINT = '/contact';

/**
 * The "notify me when back in stock" form.
 *
 * Only visible while the selected variant cannot be added to the cart. The form
 * is posted to Shopify's contact form endpoint so the store is notified by
 * email, and the customer is kept on the product page.
 *
 * @extends Component
 */
class BackInStockForm extends Component {
  /** @type {Record<string, string> | null} */
  #variantTitles = null;

  connectedCallback() {
    super.connectedCallback();

    this.closest('.shopify-section, dialog')?.addEventListener(
      ThemeEvents.variantUpdate,
      this.handleVariantUpdate
    );

    // Bound on the host element, so it survives the markup morphs below.
    this.addEventListener('submit', this.handleSubmit);

    this.confirmSubscription();
    this.updateHiddenFields();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.closest('.shopify-section, dialog')?.removeEventListener(
      ThemeEvents.variantUpdate,
      this.handleVariantUpdate
    );

    this.removeEventListener('submit', this.handleSubmit);
  }

  /**
   * Shows or hides the form when another variant is selected. The buy-buttons
   * block is rendered again on the server for the selected variant, so its
   * markup is morphed into this element, the same way the product-inventory
   * component keeps itself up to date.
   *
   * @param {CustomEvent} event - The variant update event.
   */
  handleVariantUpdate = (event) => {
    const detail = event.detail;

    if (detail.data?.newProduct) {
      this.dataset.productId = detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newSource = this.findVariantSource(detail.data?.html);
    if (newSource) morph(this, newSource, { childrenOnly: true });

    if (detail.resource?.id) {
      this.dataset.variantId = detail.resource.id;
    }

    if (typeof detail.resource?.available === 'boolean') {
      this.setAvailability(detail.resource.available);
    }

    this.updateHiddenFields();
  };

  /**
   * Submits the form in the background so the customer stays on the product
   * page. Without JavaScript the form is posted as a regular form instead.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit = async (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;

    event.preventDefault();
    this.updateHiddenFields();
    this.hideMessage('error');

    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) button.disabled = true;

    try {
      const response = await fetch(form.getAttribute('action') || CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'text/html' },
        body: new FormData(form),
      });

      if (response.ok) {
        this.showMessage('success');
        return;
      }
    } catch (error) {
      console.warn('Back in stock form could not be submitted', error);
    }

    if (button instanceof HTMLButtonElement) button.disabled = false;
    this.showMessage('error');
  };

  /** Shows the confirmation message after a regular (non JavaScript) submit. */
  confirmSubscription() {
    const url = new URL(window.location.href);

    if (!url.searchParams.has('back_in_stock')) return;

    this.showMessage('success');

    url.searchParams.delete('back_in_stock');
    url.searchParams.delete('contact_posted');

    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  /**
   * @param {Document | Element | undefined} html - The newly rendered section.
   * @returns {Element | null} The matching element for this section, if any.
   */
  findVariantSource(html) {
    if (!html) return null;

    const { sectionId } = this.dataset;

    if (sectionId) {
      const scoped = html.querySelector(`back-in-stock-form[data-section-id="${sectionId}"]`);
      if (scoped) return scoped;
    }

    return html.querySelector('back-in-stock-form');
  }

  /**
   * The form always reports the variant that is currently selected, even when
   * the customer switches variants after the page has loaded.
   */
  updateHiddenFields() {
    const variantId = this.getSelectedVariantId();
    if (!variantId) return;

    this.setHiddenValue(
      'contact[product_url]',
      `${window.location.origin}${window.location.pathname}?variant=${variantId}`
    );

    const variantTitle = this.getVariantTitles()?.[variantId];
    if (variantTitle) this.setHiddenValue('contact[variant]', variantTitle);
  }

  /** @returns {string | null} The id of the variant the customer selected. */
  getSelectedVariantId() {
    const input = this.closest('.buy-buttons-block')?.querySelector('product-form-component input[name="id"]');
    if (input instanceof HTMLInputElement && input.value) return input.value;

    return new URL(window.location.href).searchParams.get('variant') || this.dataset.variantId || null;
  }

  /** @returns {Record<string, string> | null} Variant id to variant title. */
  getVariantTitles() {
    if (this.#variantTitles) return this.#variantTitles;

    const script = this.querySelector(VARIANT_TITLES_SELECTOR);
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      this.#variantTitles = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Back in stock variant titles could not be read', error);
    }

    return this.#variantTitles;
  }

  /** @param {boolean} available - Whether the selected variant is in stock. */
  setAvailability(available) {
    const content = this.querySelector(CONTENT_SELECTOR);

    if (available) {
      this.hideMessage('success');
      this.hideMessage('error');
    }

    if (content instanceof HTMLElement) content.hidden = available;
  }

  /** @param {'success' | 'error'} type - The message to show. */
  showMessage(type) {
    const content = this.querySelector(CONTENT_SELECTOR);
    if (content instanceof HTMLElement) content.hidden = true;

    const message = this.querySelector(type === 'success' ? SUCCESS_SELECTOR : ERROR_SELECTOR);
    if (message instanceof HTMLElement) message.hidden = false;
  }

  /** @param {'success' | 'error'} type - The message to hide. */
  hideMessage(type) {
    const message = this.querySelector(type === 'success' ? SUCCESS_SELECTOR : ERROR_SELECTOR);
    if (message instanceof HTMLElement) message.hidden = true;
  }

  /**
   * @param {string} name - The name of the hidden input.
   * @param {string} value - The value to submit.
   */
  setHiddenValue(name, value) {
    const input = this.querySelector(`input[name="${name}"]`);
    if (input instanceof HTMLInputElement) input.value = value;
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
